import React, { useCallback, useEffect, useState } from "react";
import { getReturnPolicy, saveReturnPolicy, resetReturnPolicy } from "../../auth/apiReturnPolicy";
import { elakayAutoText } from "../dataElakay";
import "./ReturnPolicyEditor.css";

// Return / Replace settings of ONE product, for the Seller Edit Product page
// (mode "seller": normal, Dropshipping In, Dropshipping Out) and the Provider
// Edit Product page (mode "provider"). Loads the saved configuration, shows
// where it comes from (this product / the Provider / the Elakay policy),
// and saves only this sub-document -- nothing else of the product. The
// server validates every value and the Provider ceiling; the limits shown
// here are a convenience.
const T = (key, fallback, vars) => elakayAutoText(key, fallback, vars);

export const REASON_LABELS = {
  damaged: () => T("h_rpe_reason_damaged", "Damaged product"),
  defective: () => T("h_rpe_reason_defective", "Defective product"),
  wrong_item: () => T("h_rpe_reason_wrong_item", "Wrong item received"),
  missing_parts: () => T("h_rpe_reason_missing_parts", "Missing parts or accessories"),
  not_as_described: () => T("h_rpe_reason_not_as_described", "Not as described"),
  changed_mind: () => T("h_rpe_reason_changed_mind", "Changed mind (no defect)"),
};
const PAYER_LABELS = {
  customer: () => T("h_rpe_payer_customer", "Customer pays return shipping"),
  seller: () => T("h_rpe_payer_seller", "Seller pays return shipping"),
  provider: () => T("h_rpe_payer_provider", "Provider pays return shipping"),
};
const ERROR_LABELS = {
  INVALID_WINDOW: (e) => T("h_rpe_err_window", "Enter a whole number of days between 1 and {max}.", { max: e.max ?? 365 }),
  INVALID_REASON: () => T("h_rpe_err_reason", "Choose reasons from the list."),
  REASON_REQUIRED: () => T("h_rpe_err_reason_required", "Select at least one eligible reason."),
  INVALID_PAYER: () => T("h_rpe_err_payer", "This return shipping option is not available for this product."),
  INVALID_RESTOCKING: (e) => T("h_rpe_err_restocking", "Restocking fee must be between 0 and {max}%.", { max: e.max ?? 50 }),
  TEXT_TOO_LONG: (e) => T("h_rpe_err_text", "Text is too long (maximum {max} characters).", { max: e.max }),
  INVALID_BOOLEAN: () => T("h_rpe_err_choice", "Choose Yes or No."),
  PROVIDER_REFUSES_RETURNS: () => T("h_rpe_err_provider_returns", "The Provider does not accept returns for this product."),
  PROVIDER_REFUSES_REPLACEMENT: () => T("h_rpe_err_provider_replacement", "The Provider does not accept replacements for this product."),
  WINDOW_EXCEEDS_PROVIDER: (e) => T("h_rpe_err_provider_window", "The Provider accepts at most {max} days.", { max: e.max }),
  REASON_NOT_ACCEPTED_BY_PROVIDER: () => T("h_rpe_err_provider_reason", "Some reasons are not accepted by the Provider."),
  PROVIDER_DOES_NOT_PAY_RETURNS: () => T("h_rpe_err_provider_payer", "The Provider does not pay return shipping for this product."),
};
const errorText = (e) => (ERROR_LABELS[e.code] ? ERROR_LABELS[e.code](e) : T("h_rpe_err_generic", "Invalid value."));

const SOURCE_LABELS = {
  product: () => T("h_rpe_source_product", "Custom policy for this product"),
  provider: () => T("h_rpe_source_provider", "Inherited from the Provider"),
  platform: () => T("h_rpe_source_platform", "Elakay default policy"),
};

const toForm = (policy) => ({
  returnsAccepted: Boolean(policy?.returnsAccepted),
  replacementAccepted: Boolean(policy?.replacementAccepted),
  returnWindowDays: String(policy?.returnWindowDays ?? 30),
  eligibleReasons: [...(policy?.eligibleReasons || [])],
  returnShippingPaidBy: policy?.returnShippingPaidBy || "customer",
  restockingFeePercent: String(policy?.restockingFeePercent ?? 0),
  conditions: policy?.conditions || "",
  instructions: policy?.instructions || "",
});

const YesNo = ({ id, label, value, onChange, disabled }) => (
  <label className="rpe-field" htmlFor={id}>
    <span className="rpe-label">{label}</span>
    <select id={id} value={value ? "yes" : "no"} onChange={(e) => onChange(e.target.value === "yes")} disabled={disabled}>
      <option value="yes">{T("h_rpe_yes", "Yes")}</option>
      <option value="no">{T("h_rpe_no", "No")}</option>
    </select>
  </label>
);

const ReturnPolicyEditor = ({ productId, token, mode = "seller" }) => {
  const [state, setState] = useState({ status: "loading", data: null });
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState([]);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const apply = useCallback((data) => {
    setState({ status: "ready", data });
    setForm(toForm(data.policy));
    setErrors([]);
  }, []);

  useEffect(() => {
    if (!productId || !token) return undefined;
    let alive = true;
    setState({ status: "loading", data: null });
    getReturnPolicy(token, productId, mode).then((res) => {
      if (!alive) return;
      if (!res || res.error) setState({ status: "error", data: null });
      else apply(res.returnPolicy);
    });
    return () => { alive = false; };
  }, [productId, token, mode, apply]);

  if (!productId || !token) return null;
  const data = state.data;
  const provider = data?.provider || null;
  const fieldErrors = (field) => errors.filter((e) => e.field === field);
  const set = (key) => (value) => { setForm((f) => ({ ...f, [key]: value })); setMessage(null); };
  const toggleReason = (reason) => setForm((f) => ({
    ...f,
    eligibleReasons: f.eligibleReasons.includes(reason) ? f.eligibleReasons.filter((r) => r !== reason) : [...f.eligibleReasons, reason],
  }));

  const onSave = async () => {
    setSaving(true);
    setMessage(null);
    const res = await saveReturnPolicy(token, productId, {
      ...form,
      returnWindowDays: Number(form.returnWindowDays),
      restockingFeePercent: Number(form.restockingFeePercent || 0),
    }, mode);
    setSaving(false);
    if (!res || res.error) {
      setErrors(res?.details?.errors || []);
      setMessage({ type: "error", text: res?.code === "EXCEEDS_PROVIDER_POLICY"
        ? T("h_rpe_exceeds_provider", "This policy is more generous than the Provider's return rules. Adjust the highlighted fields.")
        : T("h_rpe_save_error", "The return policy could not be saved. Check the highlighted fields.") });
      return;
    }
    apply(res.returnPolicy);
    setMessage({ type: "success", text: T("h_rpe_saved", "Return policy saved.") });
  };

  const onReset = async () => {
    setSaving(true);
    const res = await resetReturnPolicy(token, productId, mode);
    setSaving(false);
    if (!res || res.error) {
      setMessage({ type: "error", text: T("h_rpe_save_error", "The return policy could not be saved. Check the highlighted fields.") });
      return;
    }
    apply(res.returnPolicy);
    setMessage({ type: "success", text: T("h_rpe_reset_done", "The default return policy now applies.") });
  };

  const FieldErrors = ({ field }) => fieldErrors(field).map((e, i) => <div key={`${field}-${i}`} className="rpe-error" role="alert">{errorText(e)}</div>);
  const needsWindow = form && (form.returnsAccepted || form.replacementAccepted);

  return (
    <section className="rpe" data-testid="return-policy-editor" aria-labelledby="rpe-title">
      <div className="rpe-head">
        <h3 id="rpe-title" className="rpe-title">{T("h_rpe_title", "Return & Replacement")}</h3>
        {data && <span className={`rpe-badge rpe-badge--${data.source}`} data-testid="return-policy-source">{(SOURCE_LABELS[data.source] || SOURCE_LABELS.platform)()}</span>}
      </div>

      {state.status === "loading" && <div role="status">{T("h_rpe_loading", "Loading return policy…")}</div>}
      {state.status === "error" && <div className="rpe-error" role="alert">{T("h_rpe_load_error", "The return policy could not be loaded.")}</div>}

      {state.status === "ready" && form && (
        <>
          {provider && (
            <div className="rpe-note" data-testid="return-policy-provider-limits">
              <strong>{T("h_rpe_provider_limits", "Provider return rules (you can only be stricter):")}</strong>{" "}
              {provider.returnsAccepted ? T("h_rpe_limit_returns_yes", "returns accepted") : T("h_rpe_limit_returns_no", "no returns")}
              {" · "}
              {provider.replacementAccepted ? T("h_rpe_limit_replace_yes", "replacements accepted") : T("h_rpe_limit_replace_no", "no replacements")}
              {(provider.returnsAccepted || provider.replacementAccepted) && <>{" · "}{T("h_rpe_limit_days", "up to {days} days", { days: provider.returnWindowDays })}</>}
              {" · "}{(PAYER_LABELS[provider.returnShippingPaidBy] || PAYER_LABELS.customer)()}
            </div>
          )}
          {data.providerViolations?.length > 0 && (
            <div className="rpe-warning" role="alert">{T("h_rpe_provider_tightened", "The Provider changed its return rules: the stricter Provider rules apply until you update this policy.")}</div>
          )}
          {data.supplier && (
            <div className="rpe-note">
              {data.supplier.returnAvailable
                ? T("h_rpe_supplier_accepts", "The dropshipping supplier accepts returns for this product.")
                : T("h_rpe_supplier_refuses", "The dropshipping supplier does not accept returns for this product: returns or replacements you accept are handled at your own cost.")}
            </div>
          )}

          <div className="rpe-grid">
            <YesNo id="rpe-returns" label={T("h_rpe_returns_accepted", "Returns accepted")} value={form.returnsAccepted} onChange={set("returnsAccepted")} disabled={Boolean(provider && !provider.returnsAccepted && !form.returnsAccepted)} />
            <FieldErrors field="returnsAccepted" />
            <YesNo id="rpe-replace" label={T("h_rpe_replacement_accepted", "Replacement accepted")} value={form.replacementAccepted} onChange={set("replacementAccepted")} disabled={Boolean(provider && !provider.replacementAccepted && !form.replacementAccepted)} />
            <FieldErrors field="replacementAccepted" />

            {needsWindow && (
              <>
                <label className="rpe-field" htmlFor="rpe-days">
                  <span className="rpe-label">{T("h_rpe_window_days", "Return window (days after delivery)")}</span>
                  <input id="rpe-days" type="number" min="1" step="1" max={provider ? provider.returnWindowDays : data.limits?.maxWindowDays}
                    value={form.returnWindowDays} onChange={(e) => set("returnWindowDays")(e.target.value)} />
                </label>
                <FieldErrors field="returnWindowDays" />

                <fieldset className="rpe-field rpe-reasons">
                  <legend className="rpe-label">{T("h_rpe_eligible_reasons", "Eligible reasons")}</legend>
                  {(data.reasons || []).map((reason) => {
                    const blocked = Boolean(provider && !provider.eligibleReasons.includes(reason) && !form.eligibleReasons.includes(reason));
                    return (
                      <label key={reason} className={`rpe-check${blocked ? " is-disabled" : ""}`}>
                        <input type="checkbox" checked={form.eligibleReasons.includes(reason)} disabled={blocked} onChange={() => toggleReason(reason)} />
                        <span>{(REASON_LABELS[reason] || (() => reason))()}</span>
                      </label>
                    );
                  })}
                </fieldset>
                <FieldErrors field="eligibleReasons" />

                <label className="rpe-field" htmlFor="rpe-payer">
                  <span className="rpe-label">{T("h_rpe_return_shipping", "Return shipping")}</span>
                  <select id="rpe-payer" value={form.returnShippingPaidBy} onChange={(e) => set("returnShippingPaidBy")(e.target.value)}>
                    {(data.allowedPayers || []).map((p) => <option key={p} value={p}>{(PAYER_LABELS[p] || (() => p))()}</option>)}
                  </select>
                </label>
                <FieldErrors field="returnShippingPaidBy" />

                <label className="rpe-field" htmlFor="rpe-restocking">
                  <span className="rpe-label">{T("h_rpe_restocking", "Restocking fee (%)")}</span>
                  <input id="rpe-restocking" type="number" min="0" max={data.limits?.maxRestockingPercent ?? 50} step="0.5"
                    value={form.restockingFeePercent} onChange={(e) => set("restockingFeePercent")(e.target.value)} />
                </label>
                <FieldErrors field="restockingFeePercent" />
              </>
            )}

            <label className="rpe-field rpe-field--wide" htmlFor="rpe-conditions">
              <span className="rpe-label">{T("h_rpe_conditions", "Return conditions")}</span>
              <textarea id="rpe-conditions" rows={3} maxLength={data.limits?.maxConditions ?? 1000} value={form.conditions}
                placeholder={T("h_rpe_conditions_ph", "e.g. Unused, in the original packaging, with all accessories")}
                onChange={(e) => set("conditions")(e.target.value)} />
            </label>
            <FieldErrors field="conditions" />
            <label className="rpe-field rpe-field--wide" htmlFor="rpe-instructions">
              <span className="rpe-label">{T("h_rpe_instructions", "Return instructions / policy")}</span>
              <textarea id="rpe-instructions" rows={4} maxLength={data.limits?.maxInstructions ?? 2000} value={form.instructions}
                placeholder={T("h_rpe_instructions_ph", "Explain how customers request a return or a replacement")}
                onChange={(e) => set("instructions")(e.target.value)} />
            </label>
            <FieldErrors field="instructions" />
          </div>

          {message && <div className={message.type === "error" ? "rpe-error" : "rpe-success"} role={message.type === "error" ? "alert" : "status"}>{message.text}</div>}
          <div className="rpe-actions">
            {data.configured && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onReset} disabled={saving}>
                {mode === "seller" && data.productType === "dropshipping_in" ? T("h_rpe_reset_provider", "Use the Provider's rules") : T("h_rpe_reset_platform", "Use the Elakay default")}
              </button>
            )}
            <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
              {saving ? T("h_rpe_saving", "Saving…") : T("h_rpe_save", "Save return policy")}
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default ReturnPolicyEditor;
